import { useState, useEffect } from 'react';
import { X, DollarSign, AlertCircle, CheckCircle } from 'lucide-react';
import { MaintenanceRequest } from '../../types/maintenance';

interface CFOValueConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (finalValue: number, notes?: string) => Promise<void>;
  request: MaintenanceRequest | null;
}

export default function CFOValueConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  request,
}: CFOValueConfirmModalProps) {
  const [finalValue, setFinalValue] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && request) {
      // Pre-fill with estimatedCost as reference (actualCost is set by CFO here)
      const initialValue = request.estimatedCost || 0;
      setFinalValue(initialValue.toString());
      setError(null);
    }
    if (!isOpen) {
      setFinalValue('');
      setNotes('');
      setError(null);
    }
  }, [isOpen, request]);

  if (!isOpen || !request) return null;

  const selectedQuotation = request.quotations?.find(q => q.id === request.selectedQuotationId);

  const handleSubmit = async () => {
    const value = parseFloat(finalValue);
    if (isNaN(value) || value < 0) return;
    try {
      setLoading(true);
      setError(null);
      await onConfirm(value, notes || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao concluir requisição');
    } finally {
      setLoading(false);
    }
  };

  const isValid = () => {
    const value = parseFloat(finalValue);
    return !isNaN(value) && value >= 0;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />

        <div data-testid="cfo-confirm-modal" className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <DollarSign className="h-5 w-5 mr-2 text-green-600" />
              Concluir Requisição
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Request Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-md">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Ativo:</span>{' '}
                <span className="text-gray-900">{request.equipmentId}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Descrição:</span>{' '}
                <span className="text-gray-900">{request.equipmentDescription || 'N/A'}</span>
              </div>
              <div className="col-span-2">
                <span className="font-medium text-gray-700">Problema:</span>{' '}
                <span className="text-gray-900">{request.problemDescription}</span>
              </div>
            </div>
          </div>

          {/* Selected Quotation Info (if applicable) */}
          {selectedQuotation && request.subcategory === 'compras' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <h4 className="text-sm font-medium text-green-900 mb-3 flex items-center">
                <CheckCircle className="h-4 w-4 mr-2" />
                Cotação Selecionada
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-green-700">Parceiro:</span>{' '}
                  <span className="text-green-900">{selectedQuotation.partnerName}</span>
                </div>
                <div>
                  <span className="font-medium text-green-700">Valor Cotado:</span>{' '}
                  <span className="text-green-900 font-semibold">
                    €{Number(selectedQuotation.quotedValue).toFixed(2)}
                  </span>
                </div>
                {selectedQuotation.deliveryTime && (
                  <div>
                    <span className="font-medium text-green-700">Prazo de Entrega:</span>{' '}
                    <span className="text-green-900">{selectedQuotation.deliveryTime}</span>
                  </div>
                )}
                {selectedQuotation.notes && (
                  <div className="col-span-2">
                    <span className="font-medium text-green-700">Observações:</span>{' '}
                    <span className="text-green-900">{selectedQuotation.notes}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reference Cost Info */}
          {request.estimatedCost && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="text-sm font-medium text-blue-900 mb-3">Valor de Referência</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-blue-700">Custo Estimado (Responsável):</span>
                  <span className="text-blue-900 font-medium">
                    €{Number(request.estimatedCost).toFixed(2)}
                  </span>
                </div>
                {request.actualCost && request.actualCost !== request.estimatedCost && (
                  <div className="flex justify-between">
                    <span className="text-blue-700">Custo Real (anterior):</span>
                    <span className="text-blue-900 font-medium">
                      €{Number(request.actualCost).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Final Value Input */}
          <div className="mb-6">
            <label htmlFor="finalValue" className="block text-sm font-medium text-gray-700 mb-2">
              Custo Real (€) *
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span className="text-gray-500 sm:text-sm">€</span>
              </div>
              <input
                type="text"
                inputMode="decimal"
                id="finalValue"
                value={finalValue}
                onChange={(e) => setFinalValue(e.target.value.replace(/,/g, '.'))}
                placeholder="0.00"
                data-testid="cfo-final-value"
                className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 sm:text-sm"
                disabled={loading}
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <span className="text-gray-500 sm:text-sm">EUR</span>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Insira o custo real confirmado para conclusão da requisição.
            </p>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label htmlFor="cfoNotes" className="block text-sm font-medium text-gray-700 mb-2">
              Notas do CFO (opcional)
            </label>
            <textarea
              id="cfoNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="Adicione observações sobre a confirmação do valor..."
              disabled={loading}
            />
          </div>

          {/* Warning if value differs significantly */}
          {selectedQuotation && isValid() && (
            parseFloat(finalValue) !== selectedQuotation.quotedValue && (
              <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <span className="font-medium">Atenção:</span> O valor confirmado (€{parseFloat(finalValue).toFixed(2)})
                  difere do valor cotado (€{selectedQuotation.quotedValue.toFixed(2)}).
                </div>
              </div>
            )
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isValid() || loading}
              data-testid="cfo-confirm-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? 'Processando...' : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Concluir
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
